<template lang="pug">
    .settings-page.settings-account-management
        settings-page-head
        .settings-page__body
            .settings-section-head Account Data Settings
            .row
                .col-sm-6
                    .re-inp-box
                        re-input(label="Email" type="email" v-model.trim="$v.fields.email.$model" :validations="validations.email" :v="$v.fields.email" @input="$v.fields.email.$reset")
                .col-sm-6
                    .re-inp-box
                        re-input(label="Phone" type="tel" v-model.trim="$v.fields.phone.$model" :validations="validations.phone" :v="$v.fields.phone" @input="$v.fields.phone.$reset")
            .settings-section-head Security
            .row
                .col-sm-6
                    .re-inp-box
                        re-input(label="Password" type="password" v-model.trim="$v.fields.password.$model" :validations="validations.password" :v="$v.fields.password" @input="$v.fields.password.$reset")
                .col-sm-6
                    .re-inp-box
                        re-input(label="Confirm Password" type="password" v-model.trim="$v.fields.password_confirmation.$model" :validations="validations.password_confirmation" :v="$v.fields.password_confirmation" @input="$v.fields.password_confirmation.$reset")

            template(v-if="stripeLoaded")
                .settings-section-head.mb-0 Become a Game Master
                template(v-if="!stripeCustomAccount")
                    .settings-section-subtitle.mt-3 Setup your account in Stripe
                    .row
                        .col-sm-6
                            .form-group.re-select.form-field
                                label.re-input-label.re-input-label-select Country
                                input.form-control(value="United States" disabled)
                            re-checkbox.stripe-tos(v-model="stripeTosAccepted" label="Accept Stripe Terms of Serivce")
                            re-button.btn-primary.btn-lg(@click="createStripeCustomAccount" :disabled="!stripeTosAccepted" :loading="creatingStripeCustomAccount") Add Stripe
                template(v-else)
                    .row
                        .col-md-5
                            .settings-section-subtitle.mt-3 Setup your account in Stripe
                        .col-md-7
                            .account-management__stripe-status-container
                                .account-management__stripe-status(:class="{ 'payouts-disabled': !stripeCustomAccount.payouts_enabled }") Payouts: {{ stripeCustomAccount.payouts_enabled ? 'Enabled' : 'Disabled' }}
                                .account-management__stripe-status(v-if="stripeVerified") Verified
                                a.btn.btn-link(v-else @click="getStripeVerified") Get Verified
                    .row
                        .col-sm-4
                            .re-inp-box
                                re-input(label="Account Holder Name" v-model.trim="fields.account_holder_name")
                        .col-sm-4
                            .re-inp-box
                                re-input(label="Account Number" v-model.trim="fields.account_number" :disabled="stripeBankDetailsExist && !fields.replace_bank_details")
                        .col-sm-4
                            .re-inp-box
                                re-input(label="Routing Number" v-model.trim="fields.routing_number" :disabled="stripeBankDetailsExist && !fields.replace_bank_details")
                    .form-group-error.text-right.mb-5(v-if="stripeError")
                        .error.invalid-feedback {{ stripeError }}
                    .settings-save-button.stripe-submit-button
                        re-checkbox(v-if="stripeBankDetailsExist" v-model="fields.replace_bank_details" label="Replace bank details")
                        re-button.btn-primary.btn-lg(@click="submitStripeBankDetails" :loading="submittingStripeBankDetails") Save Bank Details

            re-toast-alert(v-if="errorMessageProfile" type="danger") {{ errorMessageProfile }}

            .settings-save-button.text-right
                re-button.btn-primary.btn-lg(@click="submit" :loading="formSubmitting") Save

</template>

<script>
import ReProfileEdit from 'utils/vue/components/complex/profile/re-profile-edit/script.js'
import { Validators, parseValidations, setValidationRule, parseValidationSetup, sometimesPasswordRules, phoneRuleIfNotEmpty } from 'utils/vue/lib/validations'
import { countries } from 'utils/vue/lib/utils'
import { getFirstErrorMessage } from 'utils/vue/services/main'


const VALIDATIONS = {
    email: setValidationRule('fields.email', {
        required: {error: 'Email is required'},
        email: {error: 'Email is not valid'},
    }),
    phone: setValidationRule('fields.phone', {
        phone: {error: 'Invalid phone number', rule: phoneRuleIfNotEmpty}
    }),
    password: setValidationRule('fields.password', sometimesPasswordRules),
    password_confirmation: setValidationRule('fields.password_confirmation', {
        confirmed: {error: 'Confirm Password needs to be same as Password.', rule: Validators.sameAs('password')},
    }),
}

export default {
    name: 'lairdnd-account-edit',

    mixins: [
        ReProfileEdit
    ],

    data() {
        return {
            fields: {
                email: '',
                phone: '',
                password: '',
                password_confirmation: '',
                account_holder_name: '',
                account_number: '',
                routing_number: '',
                replace_bank_details: false,
            },

            stripeLoaded: false,
            stripeCustomAccount: null,
            stripeTosAccepted: false,
            creatingStripeCustomAccount: false,
            submittingStripeBankDetails: false,
            stripeVerified: false,
            stripeBankDetailsExist: false,
            stripeError: null,

            countries: countries.sort((a, b) => ((a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))),
        }
    },

    validations() {
        return { fields: parseValidations(VALIDATIONS) }
    },

    created() {
        this.loadStripe()
    },

    methods: {
        setupFormLocal() {
            this.validations = parseValidationSetup(VALIDATIONS, this)
            this.setupForm(VALIDATIONS, { email: '', phone: '', password: '', password_confirmation: '' })
        },

        async submit() {
            this.isSuccessPersonal = null

            this.$v.$touch()

            const payload = {
                email: this.fields.email,
                phone: this.fields.phone,
                password: this.fields.password,
            }

            if (await this.changeProfile(payload)) {
                this.isSuccessPersonal = true

                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        },

        async loadStripe() {
            const { data: { data: account } } = await axios.get(`/api/customer/stripe`)
            if (!Array.isArray(account)) {
                this.stripeCustomAccount = account

                if (account.external_accounts.data.length) {
                    const externalAccount = account.external_accounts.data[0]
                    this.fields.account_holder_name = externalAccount.account_holder_name
                    this.fields.account_number = `XXXXXXXX${externalAccount.last4}`
                    this.fields.routing_number = externalAccount.routing_number
                    this.stripeBankDetailsExist = true
                } else {
                    this.stripeBankDetailsExist = false
                }

                const currentlyDue = account.requirements.currently_due
                const externalAccountIndex = account.requirements.currently_due.indexOf('external_account')
                if (externalAccountIndex >= 0) {
                    currentlyDue.splice(externalAccountIndex, 1)
                }
                this.stripeVerified = !account.requirements.errors.length && !currentlyDue.length
            } else {
                this.stripeCustomAccount = null
            }
            this.stripeLoaded = true
        },

        async addStripeCustomAccount() {
            this.addingStripeCustomAccount = true
        },

        async createStripeCustomAccount() {
            this.creatingStripeCustomAccount = true
            try {
                await axios.post(`/api/customer/stripe`, {
                    tos_accepted: this.stripeTosAccepted,
                    country: this.stripeCountry,
                })

                await this.loadStripe()
            } finally {
                this.creatingStripeCustomAccount = false
            }
        },

        async getStripeVerified() {
            const { data: { data: { url } } } = await axios.post(`/api/customer/stripe/verify`, {
                refresh_url: window.location.href,
                return_url: window.location.href,
            })

            window.location.assign(url)
        },

        async submitStripeBankDetails() {
            this.submittingStripeBankDetails = true
            try {
                await axios.post(`/api/customer/stripe`, {
                    bank_details: {
                        account_holder_name: this.fields.account_holder_name,
                        account_number: this.fields.account_number,
                        routing_number: this.fields.routing_number,
                        replace: this.fields.replace_bank_details,
                    },
                })
                this.stripeError = null
                this.fields.replace_bank_details = false
                await this.loadStripe()
            } catch (e) {
                this.stripeError = getFirstErrorMessage(e)
            } finally {
                this.submittingStripeBankDetails = false
            }
        },

    }
}
</script>
